import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { UIMessage } from 'ai';
import { AssistantMessageList } from './assistant-message-list';
import type { AssistantRailState } from './assistant-message-list';
import { AssistantInput } from './assistant-input';
import { useAssistantContext } from '../hooks/use-assistant-context';
import { useAssistantChat } from '../hooks/use-assistant-chat';
import { useCancelRun } from '../hooks/use-cancel-run';
import { useSubmitFeedback } from '../hooks/use-submit-feedback';
import { FeedbackModal } from './messages/feedback-modal';
import type { FeedbackVote } from './messages/message-toolbar';
import { useUIStore } from '@/stores/use-ui-store';
import { ScrollRail, IconButton, Icon, Tooltip, iconWeight, Flex, message } from '@nula/core-ui';
import styles from './assistant-full-tab.module.css';

interface AssistantFullTabProps {
  activeEntityId: string | null;
  panelId: string;
}

/**
 * Find the approvalId of the first approval-requested tool part across all messages.
 *
 * Duplicated verbatim in `AssistantOverlay.tsx` and `AssistantSidebar.tsx` per the
 * M12b plan's small-diff rule. Extract to a shared hook in a future cleanup.
 */
function findPendingApprovalId(messages: UIMessage[]): string | null {
  for (const msg of messages) {
    if (msg.role !== 'assistant') continue;
    for (const part of msg.parts ?? []) {
      if (
        (part.type.startsWith('tool-') || part.type === 'dynamic-tool') &&
        (part as { state?: string }).state === 'approval-requested'
      ) {
        const approvalId = (part as { approval?: { id?: string } }).approval?.id;
        if (approvalId) return approvalId;
      }
    }
  }
  return null;
}

export function AssistantFullTab({ activeEntityId }: AssistantFullTabProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [rail, setRail] = useState<AssistantRailState | null>(null);

  const assistantContext = useAssistantContext(activeEntityId);

  const chat = useAssistantChat();
  const { cancel } = useCancelRun();
  const { submit: submitFeedback } = useSubmitFeedback();
  const [feedbackState, setFeedbackState] = useState<{
    open: boolean;
    vote: FeedbackVote;
    runId: string | undefined;
  }>({ open: false, vote: 'up', runId: undefined });

  const handleVote = useCallback(
    (vote: FeedbackVote, runId: string | undefined) => {
      setFeedbackState({ open: true, vote, runId });
    },
    [],
  );

  const handleFeedbackSubmit = useCallback(
    async (comment: string) => {
      const { submitted } = await submitFeedback({
        runId: feedbackState.runId ?? '',
        vote: feedbackState.vote,
        comment,
      });
      if (submitted) {
        message.success('Thanks — feedback sent');
      } else {
        message.error(
          'Could not send feedback. Please try again in a moment.',
        );
      }
    },
    [feedbackState.runId, feedbackState.vote, submitFeedback],
  );

  const isRunActive = chat.status === 'streaming' || chat.status === 'submitted';
  const pendingApprovalId = useMemo(() => findPendingApprovalId(chat.messages), [chat.messages]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 150);
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isRunActive) return;
    setInput('');
    chat.sendMessage({ text });
  }, [input, isRunActive, chat]);

  const handleStop = useCallback(() => {
    if (chat.runId) {
      try { cancel(chat.runId); } catch { /* not connected */ }
    }
    void chat.stop();
  }, [chat, cancel]);

  const handleApprove = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: true });
  }, [chat]);

  const handleCancel = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: false });
  }, [chat]);

  return (
    <Flex className={styles.root}>
      <Flex vertical className={styles.inner}>
        {/* Header bar — conversation history + send-to-sidebar affordances */}
        <Flex align="center" justify="end" gap="xs" className={styles.header}>
          <Tooltip title="Conversation history" placement="left">
            <IconButton
              size="md"
              aria-label="Conversation history"
              onClick={() => useUIStore.getState().setAssistantHistoryOpen(true)}
              className={styles.headerBtn}
            >
              <Icon name="history" size={16} weight={iconWeight.light} />
            </IconButton>
          </Tooltip>
        </Flex>

        {/* ScrollRail — anchored to .root (position: relative), right edge of viewport */}
        {rail && rail.items.length >= 2 && (
          <ScrollRail
            className={styles.scrollRail}
            items={rail.items}
            activeId={rail.activeId}
            onJump={rail.jumpTo}
            onScrollToTop={rail.scrollToTop}
          />
        )}
        <div className={styles.body}>
          <AssistantMessageList
            messages={chat.messages}
            onApprove={handleApprove}
            onCancel={handleCancel}
            pendingApprovalId={pendingApprovalId}
            onRailChange={setRail}
            onSuggestionClick={(text) => {
              setInput(text);
              inputRef.current?.focus();
            }}
            onVote={handleVote}
          />

          <AssistantInput
            ref={inputRef}
            className={styles.inputArea}
            value={input}
            onChange={setInput}
            onSend={handleSend}
            onStop={handleStop}
            isRunActive={isRunActive}
            assistantContext={assistantContext}
          />
        </div>
      </Flex>
      <FeedbackModal
        open={feedbackState.open}
        vote={feedbackState.vote}
        onClose={() => setFeedbackState((s) => ({ ...s, open: false }))}
        onSubmit={handleFeedbackSubmit}
      />
    </Flex>
  );
}
